import React, { useState } from "react";
import { Card, Row, Col, DatePicker, Button, Table, Statistic, Alert, Space, Tabs } from "antd";
import { FileTextOutlined, BarChartOutlined, DollarOutlined, CalendarOutlined } from "@ant-design/icons";
import { useCustom } from "@refinedev/core";
import { ITrialBalance, IIncomeStatement, AccountType, getAccountTypeLabel } from "../../../interfaces/accounting";
import dayjs from "dayjs";

const { RangePicker } = DatePicker;
const { TabPane } = Tabs;

export const FinancialReports: React.FC = () => {
    const [reportDate, setReportDate] = useState<dayjs.Dayjs>(dayjs());
    const [dateRange, setDateRange] = useState<[dayjs.Dayjs, dayjs.Dayjs]>([
        dayjs().startOf('month'),
        dayjs().endOf('month')
    ]);

    // Fetch trial balance
    const { data: trialBalanceData, isLoading: trialBalanceLoading, refetch: refetchTrialBalance } = useCustom({
        url: "reports/trialbalance",
        method: "get",
        config: {
            query: {
                asOfDate: reportDate.format('YYYY-MM-DD')
            }
        }
    });

    // Fetch profit & loss data
    const { data: profitLossData, isLoading: profitLossLoading, refetch: refetchProfitLoss } = useCustom({
        url: "reports/profitloss",
        method: "get",
        config: {
            query: {
                fromDate: dateRange[0].format('YYYY-MM-DD'),
                toDate: dateRange[1].format('YYYY-MM-DD')
            }
        }
    });

    const trialBalance = (trialBalanceData?.data?.data || []) as ITrialBalance[];
    const incomeStatement = profitLossData?.data?.data as IIncomeStatement;
    
    // Group trial balance by account type
    const groupedTrialBalance = trialBalance.reduce((groups, account) => {
        const type = getAccountTypeLabel(account.accountType);
        if (!groups[type]) {
            groups[type] = [];
        }
        groups[type].push(account);
        return groups;
    }, {} as Record<string, ITrialBalance[]>);

    // Calculate totals
    const totalDebits = trialBalance.reduce((sum, account) => sum + account.debitBalance, 0);
    const totalCredits = trialBalance.reduce((sum, account) => sum + account.creditBalance, 0);

    const trialBalanceColumns = [
        {
            title: "Account Code",
            dataIndex: "accountCode",
            width: 120,
        },
        {
            title: "Account Name",
            dataIndex: "accountName",
        },
        {
            title: "Account Type",
            dataIndex: "accountType",
            render: (value: AccountType) => getAccountTypeLabel(value),
            width: 120,
        },
        {
            title: "Debit Balance",
            dataIndex: "debitBalance",
            render: (value: number) => value > 0 ? `$${value.toLocaleString()}` : "-",
            align: "right" as const,
            width: 130,
        },
        {
            title: "Credit Balance",
            dataIndex: "creditBalance",
            render: (value: number) => value > 0 ? `$${value.toLocaleString()}` : "-",
            align: "right" as const,
            width: 130,
        },
    ];

    const generateTrialBalanceReport = () => {
        refetchTrialBalance();
    };

    const generateProfitLossReport = () => {
        refetchProfitLoss();
    };

    const TrialBalanceReport = () => (
        <Card
            title={
                <Space>
                    <FileTextOutlined />
                    Trial Balance Report
                </Space>
            }
            extra={
                <Space>
                    <DatePicker
                        value={reportDate}
                        onChange={(date) => setReportDate(date!)}
                        format="DD/MM/YYYY"
                        placeholder="As of Date"
                    />
                    <Button type="primary" onClick={generateTrialBalanceReport} loading={trialBalanceLoading}>
                        Generate Report
                    </Button>
                </Space>
            }
        >
            <Alert
                message="Trial Balance"
                description={`This report shows account balances as of ${reportDate.format('DD/MM/YYYY')}`}
                type="info"
                style={{ marginBottom: 16 }}
            />

            <Row gutter={16} style={{ marginBottom: 24 }}>
                <Col span={8}>
                    <Card size="small">
                        <Statistic
                            title="Total Debits"
                            value={totalDebits}
                            prefix="$"
                            precision={2}
                        />
                    </Card>
                </Col>
                <Col span={8}>
                    <Card size="small">
                        <Statistic
                            title="Total Credits"
                            value={totalCredits}
                            prefix="$"
                            precision={2}
                        />
                    </Card>
                </Col>
                <Col span={8}>
                    <Card size="small">
                        <Statistic
                            title="Difference"
                            value={Math.abs(totalDebits - totalCredits)}
                            prefix="$"
                            precision={2}
                            valueStyle={{ color: Math.abs(totalDebits - totalCredits) < 0.01 ? '#3f8600' : '#cf1322' }}
                        />
                    </Card>
                </Col>
            </Row>

            <Table
                dataSource={trialBalance}
                columns={trialBalanceColumns}
                rowKey="accountCode"
                loading={trialBalanceLoading}
                pagination={{
                    pageSize: 20,
                    showSizeChanger: true,
                    showTotal: (total, range) =>
                        `${range[0]}-${range[1]} of ${total} accounts`,
                }}
                summary={() => (
                    <Table.Summary.Row style={{ background: '#f0f0f0', fontWeight: 'bold' }}>
                        <Table.Summary.Cell index={0}>TOTALS</Table.Summary.Cell>
                        <Table.Summary.Cell index={1}></Table.Summary.Cell>
                        <Table.Summary.Cell index={2}></Table.Summary.Cell>
                        <Table.Summary.Cell index={3} align="right">
                            ${totalDebits.toLocaleString()}
                        </Table.Summary.Cell>
                        <Table.Summary.Cell index={4} align="right">
                            ${totalCredits.toLocaleString()}
                        </Table.Summary.Cell>
                    </Table.Summary.Row>
                )}
            />
        </Card>
    );

    const ProfitLossReport = () => {
        const profitLossColumns = [
            {
                title: "Account Code",
                dataIndex: "accountCode",
                width: 120,
            },
            {
                title: "Account Name",
                dataIndex: "accountName",
            },
            {
                title: "Amount",
                dataIndex: "amount",
                render: (value: number) => `$${value.toLocaleString()}`,
                align: "right" as const,
                width: 130,
            },
        ];

        return (
            <Card
                title={
                    <Space>
                        <BarChartOutlined />
                        Profit & Loss Statement
                    </Space>
                }
                extra={
                    <Space>
                        <RangePicker
                            value={dateRange}
                            onChange={(dates) => setDateRange(dates!)}
                            format="DD/MM/YYYY"
                        />
                        <Button type="primary" onClick={generateProfitLossReport} loading={profitLossLoading}>
                            Generate Report
                        </Button>
                    </Space>
                }
            >
                <Alert
                    message="Profit & Loss Statement"
                    description={`This report shows revenue and expenses from ${dateRange[0].format('DD/MM/YYYY')} to ${dateRange[1].format('DD/MM/YYYY')}`}
                    type="info"
                    style={{ marginBottom: 16 }}
                />

                {incomeStatement ? (
                    <>
                        <Row gutter={16} style={{ marginBottom: 24 }}>
                            <Col span={8}>
                                <Card size="small">
                                    <Statistic
                                        title="Total Revenue"
                                        value={incomeStatement.totalRevenue}
                                        prefix="$"
                                        precision={2}
                                        valueStyle={{ color: '#3f8600' }}
                                    />
                                </Card>
                            </Col>
                            <Col span={8}>
                                <Card size="small">
                                    <Statistic
                                        title="Total Expenses"
                                        value={incomeStatement.totalExpenses}
                                        prefix="$"
                                        precision={2}
                                        valueStyle={{ color: '#cf1322' }}
                                    />
                                </Card>
                            </Col>
                            <Col span={8}>
                                <Card size="small">
                                    <Statistic
                                        title="Net Income"
                                        value={incomeStatement.netIncome}
                                        prefix="$"
                                        precision={2}
                                        valueStyle={{ 
                                            color: incomeStatement.netIncome >= 0 ? '#3f8600' : '#cf1322',
                                            fontWeight: 'bold'
                                        }}
                                    />
                                </Card>
                            </Col>
                        </Row>

                        <Row gutter={16}>
                            <Col span={12}>
                                <Card
                                    title="Revenue"
                                    size="small"
                                    style={{ marginBottom: 16 }}
                                    headStyle={{ backgroundColor: '#f6ffed', color: '#52c41a' }}
                                >
                                    <Table
                                        dataSource={incomeStatement.revenueAccounts}
                                        columns={profitLossColumns}
                                        rowKey="accountCode"
                                        size="small"
                                        pagination={false}
                                        summary={() => (
                                            <Table.Summary.Row style={{ background: '#f6ffed', fontWeight: 'bold' }}>
                                                <Table.Summary.Cell index={0}>TOTAL REVENUE</Table.Summary.Cell>
                                                <Table.Summary.Cell index={1}></Table.Summary.Cell>
                                                <Table.Summary.Cell index={2} align="right">
                                                    ${incomeStatement.totalRevenue.toLocaleString()}
                                                </Table.Summary.Cell>
                                            </Table.Summary.Row>
                                        )}
                                    />
                                </Card>
                            </Col>
                            <Col span={12}>
                                <Card
                                    title="Expenses"
                                    size="small"
                                    style={{ marginBottom: 16 }}
                                    headStyle={{ backgroundColor: '#fff2f0', color: '#ff4d4f' }}
                                >
                                    <Table
                                        dataSource={incomeStatement.expenseAccounts}
                                        columns={profitLossColumns}
                                        rowKey="accountCode"
                                        size="small"
                                        pagination={false}
                                        summary={() => (
                                            <Table.Summary.Row style={{ background: '#fff2f0', fontWeight: 'bold' }}>
                                                <Table.Summary.Cell index={0}>TOTAL EXPENSES</Table.Summary.Cell>
                                                <Table.Summary.Cell index={1}></Table.Summary.Cell>
                                                <Table.Summary.Cell index={2} align="right">
                                                    ${incomeStatement.totalExpenses.toLocaleString()}
                                                </Table.Summary.Cell>
                                            </Table.Summary.Row>
                                        )}
                                    />
                                </Card>
                            </Col>
                        </Row>

                        <Card
                            style={{ 
                                backgroundColor: incomeStatement.netIncome >= 0 ? '#f6ffed' : '#fff2f0',
                                border: `2px solid ${incomeStatement.netIncome >= 0 ? '#52c41a' : '#ff4d4f'}`
                            }}
                        >
                            <Row justify="space-between" align="middle">
                                <Col>
                                    <h3 style={{ margin: 0, color: incomeStatement.netIncome >= 0 ? '#52c41a' : '#ff4d4f' }}>
                                        {incomeStatement.netIncome >= 0 ? 'Net Profit' : 'Net Loss'}
                                    </h3>
                                </Col>
                                <Col>
                                    <h2 style={{ 
                                        margin: 0, 
                                        color: incomeStatement.netIncome >= 0 ? '#52c41a' : '#ff4d4f',
                                        fontWeight: 'bold'
                                    }}>
                                        ${Math.abs(incomeStatement.netIncome).toLocaleString()}
                                    </h2>
                                </Col>
                            </Row>
                        </Card>
                    </>
                ) : (
                    <div style={{ textAlign: 'center', padding: 40 }}>
                        <Alert
                            message="No Data Available"
                            description="Click 'Generate Report' to load the Profit & Loss statement for the selected date range."
                            type="info"
                            showIcon
                        />
                    </div>
                )}
            </Card>
        );
    };

    const AccountsSummary = () => (
        <Row gutter={[16, 16]}>
            {Object.entries(groupedTrialBalance).map(([accountType, accounts]) => {
                const typeDebits = accounts.reduce((sum, acc) => sum + acc.debitBalance, 0);
                const typeCredits = accounts.reduce((sum, acc) => sum + acc.creditBalance, 0);
                
                return (
                    <Col span={8} key={accountType}>
                        <Card size="small" title={accountType}>
                            <Row gutter={8}>
                                <Col span={12}>
                                    <Statistic
                                        title="Debits"
                                        value={typeDebits}
                                        prefix="$"
                                        precision={2}
                                        valueStyle={{ fontSize: '16px' }}
                                    />
                                </Col>
                                <Col span={12}>
                                    <Statistic
                                        title="Credits"
                                        value={typeCredits}
                                        prefix="$"
                                        precision={2}
                                        valueStyle={{ fontSize: '16px' }}
                                    />
                                </Col>
                            </Row>
                            <div style={{ marginTop: 8, fontSize: '12px', color: '#666' }}>
                                {accounts.length} accounts
                            </div>
                        </Card>
                    </Col>
                );
            })}
        </Row>
    );

    return (
        <div>
            <Card 
                title={
                    <Space>
                        <DollarOutlined />
                        Financial Reports
                    </Space>
                }
                style={{ marginBottom: 24 }}
            >
                <Alert
                    message="Financial Reporting"
                    description="Generate comprehensive financial reports including Trial Balance, Profit & Loss, and Balance Sheet."
                    type="info"
                />
            </Card>

            <Tabs defaultActiveKey="trialbalance">
                <TabPane 
                    tab={
                        <span>
                            <FileTextOutlined />
                            Trial Balance
                        </span>
                    } 
                    key="trialbalance"
                >
                    <TrialBalanceReport />
                    {trialBalance.length > 0 && (
                        <Card title="Summary by Account Type" style={{ marginTop: 16 }}>
                            <AccountsSummary />
                        </Card>
                    )}
                </TabPane>
                <TabPane 
                    tab={
                        <span>
                            <BarChartOutlined />
                            Profit & Loss
                        </span>
                    } 
                    key="profitloss"
                >
                    <ProfitLossReport />
                </TabPane>
                <TabPane 
                    tab={
                        <span>
                            <CalendarOutlined />
                            Balance Sheet
                        </span>
                    } 
                    key="balancesheet"
                    disabled
                >
                    <Card>
                        <Alert
                            message="Balance Sheet"
                            description="Balance Sheet reporting will be available in a future update."
                            type="info"
                        />
                    </Card>
                </TabPane>
            </Tabs>
        </div>
    );
};